<template>
  <div class="group flex flex-col @xl:flex-row gap-1 @xl:gap-2 items-center grow">
    <div class="min-w-30 w-full @xl:w-1/4">
      <select class="criterion" aria-label="Select criterion" :name="fieldIdFieldName" v-model="selectedFieldValue" @change="criterionChangeHandler">
        <option v-for="option in criteria" :key="option.value" :value="option.value">
          {{ option.text }}
        </option>
      </select>
    </div>

    <div class="w-full @xl:w-auto @xl:grow">
      <!-- This is for the criterion text -->
      <input v-if="showCriterionText" type="text" :name="criterionTextFieldName" v-model="criterionText" aria-label="Search criterion text">

      <!-- This is for multiselect with static options -->
      <Multiselect v-else-if="showCriterionValueIds" :id="parameterizedCriterionValueIdsFieldName" :name="criterionValueIdsFieldName" :options="staticOptionsForCriterion" aria-label="Search criterion values" :initial-selected-values="props.initialValues.criterion_value_ids"></Multiselect>

      <!-- This is for multiselect with dynamic options -->
      <Multiselect v-else :id="parameterizedCriterionValueIdsFieldName" :name="listValueIdsFieldName" :options="dynamicOptionsForCriterion" aria-label="Search criterion values" :initial-selected-values="props.initialValues.list_value_ids"></Multiselect>
    </div>

    <div class="w-full @xl:w-28"><!-- Importance -->
      <select aria-label="Select criterion importance" :name="importanceFieldName" v-model="selectedImportance" :disabled="disableImportance">
        <option v-for="importance in importances" :key="importance.value" :value="importance.value" :selected="importance.value==selectedImportance">
          {{ importance.text }}
        </option>
      </select>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted } from 'vue'
  import Multiselect from "../components/Multiselect.vue"

  // CONSTANTS
  const KEYWORD = -1
  const GROUPS = -2
  const AVAILABILITY = -3
  const TAG = -5
  const PREFERRED = 13
  const GLOBAL = -999
  // Text fields are TEXT_FIELD, TEXT_AREA, DATE, or DATE_TIME
  const TEXT_FIELDS = [2, 6, 8, 9]

  const props = defineProps({
    importanceFieldName:        { type: String, required: true },
    fieldIdFieldName:           { type: String, required: true },
    criterionTextFieldName:     { type: String, required: true },
    listValueIdsFieldName:      { type: String, required: true },
    criterionValueIdsFieldName: { type: String, required: true }, /* these are the names of the fields */
    criteria:                   { type: Object, required: true }, /* each hash in criteria needs to have { name, value, and type } */
    importances:                { type: Array, required: true },
    searchableGroups:           { type: Object, required: true },
    searchableTags:             { type: Object, required: true },
    searchableAvailabilities:   { type: Object, required: true },
    programId:                  { type: Number, default: GLOBAL },
    initialValues:              { type: Object, default: () => ({
      field_id: KEYWORD,      // These names are in snake case because they're directly outputted by Rails
      criterion_text: '',
      criterion_value_ids: [],
      list_value_ids: [],
      importance: PREFERRED
    })}
  })

  function parameterize(string, separator = '-') {
    return string
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '') // Remove non-word chars (except spaces and hyphens)
      .replace(/[\s_-]+/g, separator) // Replace spaces, underscores, and hyphens with separator
      .replace(new RegExp(`^${separator}+|${separator}+$`, 'g'), '') // Remove leading/trailing separator
  }

  const selectedImportance = ref(props.initialValues.importance)
  const selectedFieldValue = ref(props.initialValues.field_id || KEYWORD)
  const criterionText = ref(props.initialValues.criterion_text || '')
  const criteria = props.criteria
  const importances = props.importances
  const searchableGroups = props.searchableGroups
  const searchableTags = props.searchableTags
  const searchableAvailabilities = props.searchableAvailabilities
  const programId = props.programId
  const parameterizedCriterionValueIdsFieldName = parameterize(props.criterionValueIdsFieldName)
  const dynamicOptionsForCriterion = ref([])

  // Map all importances to "Preferred"
  if (selectedImportance.value < 26 && selectedImportance.value > 0) {
    selectedImportance.value = 13
  }

  // Computed (reactive) functions
  const disableImportance = computed(() => {
    return (selectedFieldValue.value == GROUPS || selectedFieldValue.value == AVAILABILITY)
  })

  const showCriterionText = computed(() => {
    return (selectedFieldValue.value == KEYWORD || TEXT_FIELDS.includes(fieldType(selectedFieldValue.value)))
  })

  const showCriterionValueIds = computed(() => {
    return (selectedFieldValue.value == GROUPS || selectedFieldValue.value == AVAILABILITY || selectedFieldValue.value == TAG)
  })

  const staticOptionsForCriterion = computed(() => {
    switch (selectedFieldValue.value) {
    case GROUPS:
      return searchableGroups
    case TAG:
      return searchableTags
    case AVAILABILITY:
      return searchableAvailabilities
    }
    return []
  })

  // Regular functions
  const fieldType = (fieldId) => {
    return (criteria.filter((c) => c.value == fieldId)[0]?.type)
  }

  const criterionChangeHandler = () => {
    // Update the multiselect options here
    let params = null

    if (programId == GLOBAL) {
      params = new URLSearchParams({ id: selectedFieldValue.value })
    } else {
      params = new URLSearchParams({ id: selectedFieldValue.value, program_id: programId })
    }

    // Call the backend to get the options associated with this criterion
    fetch(`/search/criterion?${params.toString()}`)
      .then(response => response.json())
      .then(json => {
        const multiselect = document.getElementById(parameterizedCriterionValueIdsFieldName)
        if (multiselect) {
          multiselect.dispatchEvent(new CustomEvent('setOptions', { detail: json }))
        }
      })

    // Your complex field-specific logic here
  }

  onMounted(() => {
    criterionChangeHandler()
  })
</script>
